import * as React from "react";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { GridActionsCellItem } from "@mui/x-data-grid";

import { default as DataGrid } from "../../../../components/LocalizedDataGrid";

import { useTranslation } from "react-i18next";

import OccupancyPointEditPopUp from "./OccupancyPointEditPopUp";
import { ApiContext } from "../../../../App";
import EditToolbar from "./EditToolbar";

import {OCCUPANCY_KEY_TO_TEXT} from "../../../../util/keys";

export default function OccupancyPointEditor(props) {
    const { facility } = props;

    const { t } = useTranslation();

    const [rows, setRows] = React.useState([]);
    const [controllers, setControllers] = React.useState([]);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
    const [deleteCandidate, setDeleteCandidate] = React.useState();
    const [editPopUpOpen, setEditPopUpOpen] = React.useState(false);
    const [editPopUpData, setEditPopUpData] = React.useState({});
    const [bulkAddPopUpOpen, setBulkAddPopUpOpen] = React.useState(false);

    const { newOccupancyApi, controllerApi } = React.useContext(ApiContext);

    React.useEffect(() => {
        newOccupancyApi.listOccupancyPoints(
            facility,
            (data) => {
                setRows(data);
            },
            () => {
                setAlertMessage(t("Couldn't connect to server."));
                setAlertOpen(true);
            },
        );

        controllerApi.list(
            facility,
            (data) => {
                setControllers(data);
            },
            () => {
                setAlertMessage(t("Couldn't connect to server."));
                setAlertOpen(true);
            },
        );
    }, [facility]);

    const handleEditClick = (url) => {
        setEditPopUpOpen(true);
        setEditPopUpData(rows.find((item) => item.url === url));
    };

    const handleAddPointClick = () => {
        setEditPopUpData({ facility: facility });
        setEditPopUpOpen(true);
    };

    const handleBulkAddPointsClick = () => {
        setBulkAddPopUpOpen(true);
    };

    const handleEditPopUpClosedWithoutSaving = () => {
        setEditPopUpOpen(false);
    };

    const onCloseWithoutBulkImport = () => {
        setBulkAddPopUpOpen(false);
    };

    const onCloseWithBulkImport = (new_data) => {
        setRows((oldRows) => [...new_data, ...oldRows]);
        setBulkAddPopUpOpen(false);
    };

    const handleEditPopUpClosedWithUpdate = (new_data) => {
        if (rows.find((item) => item.url === new_data.url))
            setRows(
                rows.map((row) => (row.url === new_data.url ? new_data : row)),
            );
        else setRows((oldRows) => [new_data, ...oldRows]);

        setEditPopUpOpen(false);
    };

    const handleDeleteClick = (url) => {
        setDeleteCandidate(url);
        setDeleteConfirmOpen(true);
    };

    const handleDelete = (url) => {
        newOccupancyApi.Delete(
            url,
            () => {
                setRows(rows.filter((row) => row.url !== url));
            },
            () => {
                setAlertMessage(t("The point couldn't be deleted."));
                setAlertOpen(true);
            },
        );

        setDeleteCandidate(null);
    };

    const handleDuplicateClick = (url) => {
        const from_data = rows.filter((row) => row.url === url)[0];
        let to_data = { ...from_data };
        delete to_data.url;

        setEditPopUpData(to_data);
        setEditPopUpOpen(true);
    };

    const getControllerName = (params) => {
        const controller = controllers.filter(
            (item) => item.url === params.row.controller,
        );
        if (controller.length > 0) return controller[0].name;
        return "";
    };

    const columns = [
        {
            field: "controller_name",
            headerName: t("Controller"),
            flex: 1,
            minWidth: 80,
            valueGetter: getControllerName,
        },
        { field: "identifier", headerName: t("ID"), flex: 1, minWidth: 200 },
        { field: "key", headerName: t("Key"), flex: 1, minWidth: 200 ,
            renderCell: (params) => (t(OCCUPANCY_KEY_TO_TEXT[params.value]))
        },
        {
            field: "location",
            headerName: t("Location"),
            flex: 1,
            minWidth: 200,
        },
        {
            field: "actions",
            type: "actions",
            headerName: t("Actions"),
            flex: 0.5,
            minWidth: 150,
            cellClassName: "actions",
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label={t("Edit")}
                        className="textPrimary"
                        onClick={() => handleEditClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<ContentCopyIcon />}
                        label={t("Duplicate")}
                        onClick={() => handleDuplicateClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label={t("Delete")}
                        onClick={() => handleDeleteClick(id)}
                        color="inherit"
                    />,
                ];
            },
        },
    ];

    return (
        <>
            <OccupancyPointEditPopUp
                open={editPopUpOpen}
                data={editPopUpData}
                onCloseWithoutSaving={handleEditPopUpClosedWithoutSaving}
                onCloseWithUpdate={handleEditPopUpClosedWithUpdate}
                tariffs={[]}
                controllers={controllers}
                floorPlans={[]}
                pointType="occupancy"
            />
            <Dialog open={alertOpen} onClose={() => setAlertOpen(false)}>
                <DialogTitle id="alert-dialog-title">
                    {t("Problem")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAlertOpen(false)} autoFocus>
                        {t("OK")}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
            >
                <DialogTitle id="alert-dialog-title">
                    {t("Confirmation")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t("Are you sure you want to delete this point?")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setDeleteConfirmOpen(false);
                            handleDelete(deleteCandidate);
                        }}
                    >
                        {t("YES")}
                    </Button>
                    <Button
                        onClick={() => setDeleteConfirmOpen(false)}
                        autoFocus
                    >
                        {t("NO")}
                    </Button>
                </DialogActions>
            </Dialog>
            <DataGrid
                rows={rows}
                columns={columns}
                components={{
                    Toolbar: EditToolbar,
                }}
                componentsProps={{
                    toolbar: {
                        handleAddPointClick,
                        handleBulkAddPointsClick,
                        pointType: "occupancy",
                    },
                }}
                getRowId={(row) => row.url}
                experimentalFeatures={{ newEditingApi: true }}
            />
        </>
    );
}
